import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Spinner } from '@material-tailwind/react';
import { DownloadIcon, FileImageIcon, FileTextIcon, HistoryIcon, PencilIcon, SearchIcon, XIcon } from 'lucide-react';
import FloorPlanStage from './canvas-stage';
import EntityDetailsPanel from './entity-details-panel';
import { OCCUPANCY_COLORS, FILTERABLE_TYPES, canEditFloorPlan, LINKABLE_TYPES } from './constants';
import { exportStageAsPdf, exportStageAsPng } from './export';
import { useGetFloorLayoutOccupancy } from '../../custom-hooks/use-floor-layouts';
import { useAuth } from '../../services/contexts/authContext';

const FloorPlanViewer = ({
  floorId,
  onEdit,
  onHistory,
  onBack,
}) => {
  const { userDetails } = useAuth();
  const { data, isLoading, isError, error, refetch, isFetching } = useGetFloorLayoutOccupancy(floorId);
  const stageRef = useRef(null);
  const canvasWrapRef = useRef(null);

  const [selectedShape, setSelectedShape] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilters, setTypeFilters] = useState([]);
  const [statusFilters, setStatusFilters] = useState([]);
  const [highlightedIds, setHighlightedIds] = useState([]);
  const [stageSize, setStageSize] = useState({ width: 800, height: 520 });
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    const el = canvasWrapRef.current;
    if (!el) return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setStageSize({
        width: Math.max(320, Math.floor(rect.width)),
        height: Math.max(360, Math.floor(rect.height)),
      });
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isLoading, selectedShape]);

  const shapes = useMemo(() => data?.shapes || [], [data?.shapes]);
  const canvas = useMemo(() => data?.canvas || {}, [data?.canvas]);
  const floor = data?.floor;

  const backgroundUrl = useMemo(() => {
    if (canvas.useFloorPlanAsBackground !== false && floor?.floorPlan?.url) {
      return floor.floorPlan.url;
    }
    return canvas.backgroundImage?.url || null;
  }, [canvas, floor]);

  const filteredShapes = useMemo(() => {
    return shapes.filter((s) => {
      if (typeFilters.length && !typeFilters.includes(s.elementType)) return false;
      if (statusFilters.length) {
        if (!LINKABLE_TYPES.has(s.elementType)) return true;
        if (!s.occupancyStatus || !statusFilters.includes(s.occupancyStatus)) return false;
      }
      return true;
    });
  }, [shapes, typeFilters, statusFilters]);

  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      setHighlightedIds([]);
      return;
    }
    const matches = shapes.filter((s) => {
      const code = s.occupancy?.entity?.code || s.occupancy?.entity?.seatCode || s.text || '';
      return code.toLowerCase().includes(q) || s.id.toLowerCase().includes(q);
    });
    setHighlightedIds(matches.map((m) => m.id));
    if (matches.length === 1) setSelectedShape(matches[0]);
  }, [search, shapes]);

  const toggleType = (value) => {
    setTypeFilters((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };
  const toggleStatus = (value) => {
    setStatusFilters((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const exportTitle = floor?.floorCode
    ? `Floor ${floor.number} (${floor.floorCode})`
    : 'Floor plan';
  const exportBase = `floor-plan-${floor?.floorCode || floorId || 'export'}`;

  const handleExportPng = () => {
    exportStageAsPng(stageRef.current, { filename: `${exportBase}.png` });
    setExportOpen(false);
  };
  const handleExportPdf = () => {
    exportStageAsPdf(stageRef.current, { filename: exportBase, title: exportTitle });
    setExportOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="h-7 w-7" />
      </div>
    );
  }

  if (isError && error?.response?.status === 404) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-sm text-gray-800">No published floor plan</p>
        <p className="mt-1 text-xs text-gray-400">This floor does not have a published layout yet.</p>
        {canEditFloorPlan(userDetails?.role) && onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="mt-4 h-8 rounded-full bg-gray-900 px-3 text-xs text-white hover:bg-gray-800"
          >
            Create layout
          </button>
        )}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-16 text-center text-sm text-red-600">
        Failed to load floor plan.
        <button type="button" className="ml-2 text-xs underline" onClick={() => refetch()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-6.5rem)] min-h-[480px] flex-col">
      {/* Compact top bar */}
      <header className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-gray-200 pb-2">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="text-xs text-gray-500 hover:text-gray-900"
            >
              ← Back
            </button>
          )}
          <div className="min-w-0">
            <h1 className="truncate text-sm font-medium text-gray-900">
              Floor {floor?.number}
              {floor?.floorCode ? <span className="font-normal text-gray-500"> · {floor.floorCode}</span> : null}
            </h1>
            {floor?.building?.name && (
              <p className="truncate text-[11px] text-gray-400">{floor.building.name}</p>
            )}
          </div>
          {isFetching && <Spinner className="h-3.5 w-3.5" />}
        </div>

        <div className="relative flex items-center">
          <SearchIcon size={13} className="pointer-events-none absolute left-2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search code…"
            className="h-8 w-40 border-0 border-b border-gray-200 bg-transparent pl-7 pr-2 text-xs outline-none focus:border-gray-500 sm:w-48"
          />
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => setExportOpen((v) => !v)}
            className="inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-xs text-gray-600 hover:bg-gray-100 hover:text-gray-900"
          >
            <DownloadIcon size={14} /> Export
          </button>
          {exportOpen && (
            <div className="absolute right-0 z-20 mt-1 w-40 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-sm">
              <button
                type="button"
                onClick={handleExportPng}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
              >
                <FileImageIcon size={14} /> PNG image
              </button>
              <button
                type="button"
                onClick={handleExportPdf}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
              >
                <FileTextIcon size={14} /> PDF
              </button>
            </div>
          )}
        </div>

        {onHistory && (
          <button
            type="button"
            onClick={onHistory}
            className="inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-xs text-gray-600 hover:bg-gray-100 hover:text-gray-900"
          >
            <HistoryIcon size={13} /> History
          </button>
        )}

        {canEditFloorPlan(userDetails?.role) && onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex h-8 items-center gap-1.5 rounded-full bg-gray-900 px-3 text-xs text-white hover:bg-gray-800"
          >
            <PencilIcon size={13} /> Edit
          </button>
        )}
      </header>

      {/* Compact filters — single row */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-gray-100 py-1.5">
        <div className="flex flex-wrap items-center gap-1">
          {Object.entries(OCCUPANCY_COLORS).map(([key, meta]) => (
            <span key={key} className="inline-flex items-center gap-1 text-[10px] text-gray-500">
              <span className="inline-block h-2 w-2 rounded-sm" style={{ background: meta.stroke }} />
              {meta.label}
            </span>
          ))}
        </div>
        <div className="hidden h-3 w-px bg-gray-200 sm:block" />
        <div className="flex flex-wrap items-center gap-1">
          {FILTERABLE_TYPES.map((t) => {
            const active = typeFilters.includes(t.value);
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => toggleType(t.value)}
                className={`rounded-full px-2.5 py-0.5 text-[11px] transition-colors ${active ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'
                  }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <div className="hidden h-3 w-px bg-gray-200 sm:block" />
        <div className="flex flex-wrap items-center gap-1">
          {Object.entries(OCCUPANCY_COLORS).map(([key, meta]) => {
            const active = statusFilters.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleStatus(key)}
                className={`rounded-full px-2.5 py-0.5 text-[11px] transition-colors ${active ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'
                  }`}
              >
                {meta.label}
              </button>
            );
          })}
        </div>
        {(typeFilters.length > 0 || statusFilters.length > 0) && (
          <button
            type="button"
            onClick={() => { setTypeFilters([]); setStatusFilters([]); }}
            className="inline-flex items-center gap-0.5 rounded-full px-1.5 text-[11px] text-gray-400 hover:text-gray-700"
          >
            <XIcon size={12} /> Clear
          </button>
        )}
      </div>

      {/* Canvas fills remaining height */}
      <div className="mt-2 flex min-h-0 flex-1 gap-3">
        <div ref={canvasWrapRef} className="min-h-0 min-w-0 flex-1 overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
          <FloorPlanStage
            stageRef={stageRef}
            width={stageSize.width}
            height={stageSize.height}
            canvas={canvas}
            shapes={filteredShapes}
            backgroundUrl={backgroundUrl}
            colorByOccupancy
            highlightedIds={highlightedIds}
            selectedId={selectedShape?.id}
            editable={false}
            fitToContent
            onSelectShape={(shape) => {
              if (LINKABLE_TYPES.has(shape.elementType)) setSelectedShape(shape);
              else setSelectedShape(null);
            }}
            onStageClick={() => setSelectedShape(null)}
          />
        </div>
        {selectedShape && (
          <div className="w-64 shrink-0 overflow-y-auto">
            <EntityDetailsPanel shape={selectedShape} onClose={() => setSelectedShape(null)} />
          </div>
        )}
      </div>
    </div>
  );
};

export default FloorPlanViewer;
